/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-02 16:35:33
 **/

import React from "react";
import isEmpty from "lodash-es/isEmpty";

import { Column, Row, Button, Typography } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T, _P } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";

import { usePhoneCall, usePopover } from "@/frameworks/core/hooks";
import { POPOVER_ID } from "@/frameworks/core/commons";

import DialPad from "./DialPad";
import IdleCommands from "./IdleCommands";
import Commands from "./Commands";

import voiceRecordingCall from "@/assets/lotties/media/voice_recording_call.json";
import { AddCalleesPopup } from "../../commons";

type CommandBarProps = {
	// empty
};

export type CommandsProps = {
	callID: string;
	callStatus: PushServices.Call.CallStatus;
	// @ts-ignore
	lottieOptions: LottiePlayer.Options;
	overCallees: boolean;
	isGroupCallHistory?: boolean;
	onAddUser(ev: React.MouseEvent<HTMLButtonElement>): void;
	callerID?: string;
} & Partial<WebServices.People.List.User>;

const LOGTAG = "#CommandBar#";
const MAX_ALLOW_CALLEES = 3;
const optionsRecording = {
	loop: true,
	autoplay: false,
	animationData: voiceRecordingCall,
	rendererSettings: {
		preserveAspectRatio: "xMidYMid slice"
	}
};

export default function CommandBar(
	props: PushServices.Call.PhoneDetailProps & {
		onUnBlock?: () => void;
		isBlockedByMe?: boolean;
		visibleSubCommand?: boolean;
	}
) {
	const popover = usePopover();
	const phoneCall = usePhoneCall();
	const callID = phoneCall.state.id[0];
	const callees = phoneCall.state.callees[0];
	const callStatus = phoneCall.state.status(callID)[0];
	const isGroupCallHistory = phoneCall.state.phone_detail[0]?.tabID === "group-call-history";

	const handleAddUser = async (ev: React.MouseEvent<HTMLButtonElement>) =>
		popover.show({
			popoverID: POPOVER_ID.ADD_CALL_USER,
			content: <AddCalleesPopup callID={callID} />,
			anchorEl: ev.currentTarget,
			direction: {
				x: "right",
				y: "top"
			}
		});

	return (
		callees.length > 0 &&
		callees.length < 5 && (
			<Maybe test={callees[0].userID !== Configure.userID} fallback={<Row gap={10} />}>
				<Maybe test={props.visibleSubCommand}>
					<Row.ul horizonAlign="center" gap={8}>
						<Maybe
							test={!isEmpty(callStatus)}
							fallback={
								<IdleCommands
									{...props}
									callID={callID}
									callStatus={callStatus}
									lottieOptions={optionsRecording}
									isGroupCallHistory={isGroupCallHistory}
									overCallees={callees.length > MAX_ALLOW_CALLEES}
									onAddUser={handleAddUser}
								/>
							}
						>
							<Commands
								{...props}
								callID={callID}
								callStatus={callStatus}
								lottieOptions={optionsRecording}
								overCallees={callees.length > MAX_ALLOW_CALLEES}
								isGroupCallHistory={isGroupCallHistory}
								callerID={props.callerID}
								onAddUser={handleAddUser}
							/>
						</Maybe>
					</Row.ul>
				</Maybe>
				<Column.div verticalAlign="center" horizonAlign="center" gap={16}>
					<Maybe test={props.isBlockedByMe} fallback={<DialPad {...props} callID={callID} />}>
						<Typography.p typoSize="Body3" align="center">
							{_P("차단된 사용자 입니다. <br />차단을 해제해야 소통이 가능합니다.")}
						</Typography.p>
						<Button filled="secondary" size="small" css={{ minWidth: 82 }} onClick={props.onUnBlock}>
							{_T("차단해제")}
						</Button>
					</Maybe>
				</Column.div>
			</Maybe>
		)
	);
}
